/** @flow */

import { takeLatest, put, all, call } from "redux-saga/effects";
import http from "@http";

import { toast } from "react-toastify";
import type { Action } from "redux-action";
import {
  GetScheduledReportData,
  LogRunReport,
  SubmitScheduleReportSettings,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";

export function* onGetScheduledReportData({ payload }: Action<Object>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.scheduledReportId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/reports/client/${payload.clientId}/employee/${payload.employeeId}/scheduled-reports/${payload.scheduledReportId}`,
    );

    yield put(actionCreators.GetScheduledReportDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetScheduledReportDataError(e.response));
  }
}

export function* onLogRunReport({
  payload,
}: Action<{ clientId: number, reportType: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportType) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `api/v1/reports/client/${payload.clientId}/report-run/${payload.reportType}`,
    );

    yield put(actionCreators.LogRunReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.LogRunReportError(e.response));
  }
}
export function* onSubmitScheduleReportSettings({
  payload,
}: Action<Object>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.formData ||
      !payload.employeeClientId
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { clientId, employeeId, formData, employeeClientId } = payload;
    const scheduledReportId = formData.scheduledReportId;

    let url;
    let method = payload.formData.scheduledReportId ? http.put : http.post;

    if (payload.formData.scheduledReportId) {
      url = `api/v1/reports/client/${employeeClientId}/employee/${employeeId}/scheduled-reports/${scheduledReportId.id}`;
    } else {
      url = `api/v1/reports/client/${employeeClientId}/employee/${employeeId}/scheduled-reports`;
    }
    const response = yield call(method, url, { ...formData });
    yield all([
      put(
        toast.success(
          formData.status ? "Schedule saved successfully." : "Schedule disabled successfully.",
        ),
      ),
      put(actionCreators.SubmitScheduleReportSettingsSuccess(response.data)),
      put(actionCreators.GetAbsenceMySavedReports({ clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitScheduleReportSettingsError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([takeLatest(LogRunReport, onLogRunReport)]);
  yield all([takeLatest(GetScheduledReportData, onGetScheduledReportData)]);
  yield all([takeLatest(SubmitScheduleReportSettings, onSubmitScheduleReportSettings)]);
}
